import { useState } from 'react';
import { Loader2, Play, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

export function BROSInvoiceXeroLinkagePanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.brosInvoiceXeroLinkage();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const bi = result?.bros_invoices;
  const mi = result?.main_invoices_bros_source;
  const xi = result?.xero_invoices_context;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">BR OS Invoice / Xero PDF Linkage</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Checks whether BR OS invoices carry a Xero InvoiceID (or equivalent reference)
          that would allow server-side PDF fetch from Xero.
        </p>
        <Button onClick={run} disabled={loading} size="sm">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
          Run Diagnostic
        </Button>

        {result && (
          <div className="space-y-4">
            {/* Verdict */}
            <div className={`flex items-start gap-2 p-3 rounded-lg border ${
              result.linkable
                ? 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-950'
                : 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950'
            }`}>
              {result.linkable
                ? <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
                : <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              }
              <p className="text-sm" data-testid="linkage-verdict">{result.verdict}</p>
            </div>

            {/* Counts grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {bi && (
                <div className="border rounded-lg p-3 space-y-1">
                  <p className="text-sm font-medium">bros_invoices</p>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <span className="text-muted-foreground">Total</span>
                    <span className="tabular-nums" data-testid="bros-total">{fmtInt(bi.total)}</span>
                    <span className="text-muted-foreground">With Xero ref</span>
                    <span className="tabular-nums">{fmtInt(bi.with_xero_ref)}</span>
                    <span className="text-muted-foreground">Without</span>
                    <span className="tabular-nums">{fmtInt(bi.without_xero_ref)}</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Checked: {bi.checked_fields?.join(', ')}
                  </p>
                </div>
              )}

              {mi && (
                <div className="border rounded-lg p-3 space-y-1">
                  <p className="text-sm font-medium">invoices (source=bros)</p>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <span className="text-muted-foreground">Total</span>
                    <span className="tabular-nums">{fmtInt(mi.total)}</span>
                    <span className="text-muted-foreground">With external_id</span>
                    <span className="tabular-nums">{fmtInt(mi.with_external_id)}</span>
                    <span className="text-muted-foreground">With xero_tenant_id</span>
                    <span className="tabular-nums">{fmtInt(mi.with_xero_tenant_id)}</span>
                  </div>
                </div>
              )}

              {xi && (
                <div className="border rounded-lg p-3 space-y-1">
                  <p className="text-sm font-medium">Xero invoices (context)</p>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <span className="text-muted-foreground">Total</span>
                    <span className="tabular-nums">{fmtInt(xi.total)}</span>
                    <span className="text-muted-foreground">With external_id</span>
                    <span className="tabular-nums">{fmtInt(xi.with_external_id)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Sample docs */}
            {result.sample && result.sample.length > 0 && (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-muted-foreground">
                  Sample BR OS invoices ({result.sample.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">bros_id</th>
                        <th className="text-left px-2 py-1.5 font-medium">type</th>
                        <th className="text-left px-2 py-1.5 font-medium">company</th>
                        <th className="text-right px-2 py-1.5 font-medium">total_amount</th>
                        <th className="text-center px-2 py-1.5 font-medium">Xero ref?</th>
                        <th className="text-left px-2 py-1.5 font-medium">Stored fields</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.sample.map((s) => (
                        <tr key={s.bros_id} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{s.bros_id}</td>
                          <td className="px-2 py-1">{s.type || '-'}</td>
                          <td className="px-2 py-1">{s.company || '-'}</td>
                          <td className="px-2 py-1 text-right tabular-nums">
                            {s.total_amount != null ? Number(s.total_amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                          </td>
                          <td className="px-2 py-1 text-center">
                            {s.has_xero_ref
                              ? <Badge className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">{s.xero_ref_field}</Badge>
                              : <span className="text-muted-foreground">none</span>
                            }
                          </td>
                          <td className="px-2 py-1 text-[10px] text-muted-foreground max-w-xs truncate" title={s.stored_fields?.join(', ')}>
                            {s.stored_fields?.join(', ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            <p className="text-[10px] text-muted-foreground">
              Generated: {result.generated_at}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
